import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ChevronLeft, ChevronRight, Shield, Users } from "lucide-react";
import Navbar from "../../../components/Navbar";
import { useNavigate } from "react-router-dom";
import PartnerCard from "../../../components/Partners/PartnerCard";
import { useEffect } from "react";
import axios from "axios";
import Config from "../../../Config";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";
import PermissionAwareButton from "../../../components/Permissions/PermissionAwareButton";
import { PERMISSIONS } from "../../../config/permissions";
import PartnersPermissionTest from "../../../components/Permissions/PartnersPermissionTest";

const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <div className="flex flex-col gap-4 justify-between items-center p-4 bg-white rounded-xl shadow-sm sm:flex-row">
    <p className="text-sm text-gray-600">
      Showing page <span className="font-medium">{currentPage}</span> of{" "}
      <span className="font-medium">{totalPages}</span>
    </p>
    <div className="flex gap-2 items-center">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-lg transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="w-5 h-5 text-gray-600" />
      </button>
      <div className="flex gap-1 items-center">
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i + 1}
            onClick={() => onPageChange(i + 1)}
            className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-colors ${
              currentPage === i + 1
                ? "bg-[#FF6C6C] text-white"
                : "hover:bg-gray-100 text-gray-600"
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-5 h-5 text-gray-600" />
      </button>
    </div>
  </div>
);

const Partners = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const partnersPerPage = 5;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPermissionTest, setShowPermissionTest] = useState(false);
  
  // Permission checks
  const { 
    canPerform, 
    isAuthenticated,
    isMainVendor 
  } = usePermissionCheck();

  // Check specific permissions
  const canViewPartners = canPerform(PERMISSIONS.MODULES.PARTNER_MANAGEMENT, PERMISSIONS.ACTIONS.VIEW_PARTNERS) || isMainVendor;
  const canCreatePartners = canPerform(PERMISSIONS.MODULES.PARTNER_MANAGEMENT, PERMISSIONS.ACTIONS.CREATE_PARTNER) || isMainVendor;
  const canEditPartners = canPerform(PERMISSIONS.MODULES.PARTNER_MANAGEMENT, PERMISSIONS.ACTIONS.EDIT_PARTNER) || isMainVendor;
  const canDeletePartners = canPerform(PERMISSIONS.MODULES.PARTNER_MANAGEMENT, PERMISSIONS.ACTIONS.DELETE_PARTNER) || isMainVendor;

  // Debug logging for permission checks
  console.log('Partners Permission Debug:', {
    isAuthenticated,
    isMainVendor,
    canViewPartners,
    canCreatePartners,
    canEditPartners,
    canDeletePartners
  });

  //* Fetch partners from API
  const fetchPartners = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${Config.backendDomain}vendor/partner`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("dealboxx_token")}`,
        },
      });

      const data = await response?.data;
      if (
        data?.data?.status_code === "000" &&
        data?.data?.in_error === false
      ) {
        setPartners(data?.data?.data);
        console.log("Partners:", data?.data?.data);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        toast.error(data?.data?.reason || "Failed to fetch partners");
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(
        error?.response?.data?.reason || "Failed to fetch partners"
      );
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    if (canViewPartners) {
      fetchPartners();
    }
  }, [canViewPartners]);

  const totalPages = Math.max(1, Math.ceil(partners.length / partnersPerPage));
  const currentPartners = partners.slice(
    (currentPage - 1) * partnersPerPage,
    currentPage * partnersPerPage
  );

  const handleEditPartner = (partner) => {
    if (canEditPartners) {
      navigate(`/edit-partner/${partner.partner_slug}`);
    } else {
      toast.error("You don't have permission to edit partners");
    }
  };

  const handleDeletePartner = async(partner) => {
    if (!canDeletePartners) {
      toast.error("You don't have permission to delete partners");
      return;
    }

    try{
      setIsDeleting(true);
      const response = await axios.delete(`${Config.backendDomain}vendor/partner/${partner.partner_slug}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("dealboxx_token")}`,
        },
      });
      const data = await response?.data;
      if(data?.data?.status_code === "000" && data?.data?.in_error === false){
        toast.success("Partner deleted successfully");
        fetchPartners();
      }else{
        toast.error(data?.data?.reason || "Failed to delete partner");
      }
      
    }catch(error){
      setIsDeleting(false);
      toast.error(error?.response?.data?.reason || "Failed to delete partner");
    }finally{
      setIsDeleting(false);
    }
  };

  // Access denied screen for users without view permissions
  if (!isAuthenticated || !canViewPartners) {
    return (
      <div className="bg-white">
        <Navbar page="Partners" />
        <div className="flex justify-center items-center min-h-[80vh] bg-gray-50">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-red-100 rounded-full">
              <Shield className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">Access Restricted</h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You don't have permission to view partner management. Please contact your administrator to request access to the Partner Management module.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2 items-center mb-2">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Required Permission:</span>
              </div>
              <p className="text-sm text-blue-700">View Partners in Partner Management</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/dashboard')}
              className="mt-6 px-6 py-3 bg-[#FF6C6C] text-white rounded-lg hover:bg-[#FF6C6C]/90 transition-colors font-medium"
            >
              Return to Dashboard
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  if(isLoading){
    return <Loader />
  }

  return (
    <>
      <Navbar page="Partners" />
      <div className="min-h-screen bg-gray-50/50">
        <div className="px-4 py-8 mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 justify-between mb-8 sm:flex-row sm:items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Vendor Partners
              </h1>
              <p className="mt-1 text-gray-600">
                Manage and view all your vendor partners
              </p>
            </div>
            
            <div className="flex gap-2 items-center">
              {/* <button
                onClick={() => setShowPermissionTest(!showPermissionTest)}
                className="px-3 py-2 text-sm text-white bg-blue-500 rounded-lg transition-colors hover:bg-blue-600"
              >
                {showPermissionTest ? 'Hide' : 'Show'} Permission Test
              </button> */}
              
              <PermissionAwareButton
                permission={{
                  module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
                  action: PERMISSIONS.ACTIONS.CREATE_PARTNER
                }}
                fallbackForMainVendor={true}
                onClick={() => navigate("/add-partner")}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white rounded-xl font-medium shadow-sm hover:shadow transition-all duration-200"
              >
                <Plus className="w-5 h-5" />
                <span>Add New Partner</span>
              </PermissionAwareButton>
            </div>
          </div>

          {/* Permission Test Section */}
          {showPermissionTest && (
            <div className="mb-8">
              <PartnersPermissionTest />
            </div>
          )}

          <div className="space-y-4">
            <AnimatePresence>
              {partners.length > 0 ? (
                <>
                  {currentPartners.map((partner) => (
                    <PartnerCard
                      key={partner.id}
                      partner={partner}
                      onEdit={handleEditPartner}
                      onDelete={handleDeletePartner}
                      isDeleting={isDeleting}
                      canEdit={canEditPartners}
                      canDelete={canDeletePartners}
                    />
                  ))}

                  {partners.length > partnersPerPage && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  )}
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="flex flex-col justify-center items-center p-8 bg-white rounded-2xl shadow-sm"
                >
                  <div className="flex justify-center items-center mb-6 w-24 h-24 bg-pink-50 rounded-full">
                    <svg
                      className="w-12 h-12 text-[#FF6C6C]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-gray-900">No Partners Yet</h3>
                  <p className="mb-6 max-w-md text-center text-gray-500">
                    {
                      canCreatePartners ? (
                        "You haven't added any partners to your network. Start collaborating by adding your first partner."
                      ) : (
                        "You don't have permission to add partners. Please contact your administrator to request access to the Partner Management module."
                      )
                    }
                  </p>
                  
                  {/* Permission-aware Create Button in Empty State */}
                  <PermissionAwareButton
                    permission={{
                      module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
                      action: PERMISSIONS.ACTIONS.CREATE_PARTNER
                    }}
                    fallbackForMainVendor={true}
                    onClick={() => navigate("/add-partner")}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white rounded-xl font-medium shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Add Your First Partner</span>
                  </PermissionAwareButton>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
};

export default Partners;
